<section #marketplaceShell class="mp-shell" role="dialog" aria-modal="true" aria-label="Marketplace" pFocusTrap>
    <header class="mp-header">
        <div class="mp-breadcrumbs">
            @if (selectedItem) {
                <button type="button" class="mp-breadcrumb-link" (click)="showHome()">
                    <fa-icon icon="chevron-left" aria-hidden="true"></fa-icon>
                    Marketplace
                </button>
                <span class="mp-breadcrumb-divider">/</span>
                <strong>{{selectedItem.title}}</strong>
            } @else {
                <span class="mp-header-title">Marketplace</span>
            }
        </div>
        <div class="mp-header-actions">
            <button #marketplaceInitialFocus type="button" class="mp-header-button" (click)="openRequests()">
                Requests
                @if (!isRequestsLoading && !requestsLoadError && requests.length > 0) {
                    <span class="mp-request-count">{{requests.length}}{{hasMoreRequests ? '+' : ''}}</span>
                }
            </button>
            <button type="button" class="mp-icon-button" aria-label="Close Marketplace" [disabled]="interactionSubmissionPending" (click)="closeMarketplace()">
                <fa-icon icon="times" aria-hidden="true"></fa-icon>
            </button>
        </div>
    </header>

    <div #marketplaceContent class="mp-content">
        @if (isMarketplaceLoading) {
            <main class="mp-home mp-load-state" aria-live="polite">
              <p-progressSpinner></p-progressSpinner>
              <h3>Loading Marketplace ...</h3>
            </main>
        } @else if (marketplaceLoadError) {
            <main class="mp-home mp-load-state">
                <div class="mp-error-state mp-error-state--page" role="alert">
                    <fa-icon icon="exclamation-circle" aria-hidden="true"></fa-icon>
                    <h3>Marketplace unavailable</h3>
                    <p>{{marketplaceLoadError}}</p>
                    <button pButton type="button" class="mp-primary-button" (click)="retryMarketplaceLoad()">Try again</button>
                </div>
            </main>
        } @else if (!selectedItem) {
            <main class="mp-home">
                <section class="mp-intro">
                    <p class="mp-eyebrow">Marketplace</p>
                    <h1>Expand your webmate</h1>
                    <p>Discover ways to do more with webmate: features and capacity, expert services, and what we’re exploring next.</p>
                </section>

                <label class="mp-search">
                    <fa-icon icon="search" aria-hidden="true"></fa-icon>
                    <input type="search" [(ngModel)]="searchQuery" placeholder="Search the marketplace…" aria-label="Search the marketplace">
                </label>

                <div class="mp-tag-pills" role="group" aria-label="Filter Marketplace by tag">
                    <button type="button" [class.active]="!activeTag" [attr.aria-pressed]="!activeTag" (click)="selectTag()">All</button>
                    @for (tag of featuredTags; track tag.key) {
                        <button type="button" [class.active]="activeTag === tag.key" [attr.aria-pressed]="activeTag === tag.key" (click)="selectTag(tag.key)">{{tag.label}}</button>
                    }
                </div>

                @if (isResultsMode) {
                    <section class="mp-section">
                        <div class="mp-section-heading">
                            <div>
                                <p class="mp-eyebrow">Browse</p>
                                <h2>{{searchQuery.trim() ? 'Search results' : activeTagDefinition?.label}}</h2>
                            </div>
                            <span>{{filteredItems.length}} {{filteredItems.length === 1 ? 'item' : 'items'}}</span>
                        </div>
                        @if (filteredItems.length > 0) {
                            <div class="mp-card-grid">
                                @for (item of filteredItems; track item.id) {
                                    <wm-marketplace-card [item]="item" [pricingVisible]="pricingVisible" [primaryActionLabel]="actionLabel(item, item.primaryAction)" [primaryActionDisabled]="item.primaryAction.kind === 'signalInterest' && (isInterestSubmissionPending(item) || isInterested(item))" (detail)="showDetails($event)" (action)="handleCardAction($event)"></wm-marketplace-card>
                                }
                            </div>
                        } @else {
                            <div class="mp-empty-state">
                                <fa-icon icon="search" aria-hidden="true"></fa-icon>
                                <h3>No marketplace items found</h3>
                                <p>Try another search term or tag.</p>
                                <button type="button" class="mp-secondary-button" (click)="searchQuery = ''; selectTag()">Show everything</button>
                            </div>
                        }
                    </section>
                } @else {
                    @if (featuredItem; as hero) {
                        <section class="mp-featured">
                            <div class="mp-featured__copy">
                                <p class="mp-featured__eyebrow">★ Featured</p>
                                <div class="mp-featured__badges">
                                    <span>{{hero.kindLabel}}</span>
                                    @if (hero.presentationKind === 'roadmapIdea') {
                                        <span>{{hero.statusLabel}}</span>
                                    }
                                </div>
                                <h2>{{hero.title}}</h2>
                                <p>{{hero.valueProp}}</p>
                                <div class="mp-featured__actions">
                                    <button type="button" class="mp-featured__primary" (click)="showDetails(hero)">View details</button>
                                    @for (action of hero.actions; track action.id) {
                                        @if (action.kind === 'requestService') {
                                            <button type="button" class="mp-featured__secondary" (click)="handleAction(hero, action)">{{action.label}}</button>
                                        }
                                    }
                                </div>
                            </div>
                            <div class="mp-featured__stat">
                                @if (hero.featuredPresentation?.metric; as metric) {
                                    <strong>{{metric.value}}</strong>
                                    <span>{{metric.label}}</span>
                                }
                                @if (hero.featuredPresentation?.visual === 'loadProfile') {
                                    <svg viewBox="0 0 240 70" role="img" aria-label="Example load profile">
                                        <path class="mp-chart-area" d="M4 61 L40 42 L76 42 L104 19 L124 42 L184 42 L236 61 L236 68 L4 68 Z"></path>
                                        <path class="mp-chart-line" d="M4 61 L40 42 L76 42 L104 19 L124 42 L184 42 L236 61"></path>
                                    </svg>
                                }
                                <b>
                                    @if (hero.presentationKind === 'roadmapIdea') {
                                        Shape what comes next
                                    } @else if (pricingVisible) {
                                        {{hero.commercialSummary}} @if (hero.featuredPresentation?.commercialQualifier) { <small>· {{hero.featuredPresentation?.commercialQualifier}}</small> }
                                    } @else {
                                        Pricing according to your agreement
                                    }
                                </b>
                            </div>
                        </section>
                    }

                    <section class="mp-section">
                        <div class="mp-section-heading">
                            <div>
                                <h2>Recommended for you</h2>
                                <p>Based on the capabilities teams commonly add to active testing projects.</p>
                            </div>
                        </div>
                        <div class="mp-card-grid">
                            @for (item of recommendedItems; track item.id) {
                                <wm-marketplace-card [item]="item" [pricingVisible]="pricingVisible" [primaryActionLabel]="actionLabel(item, item.primaryAction)" [primaryActionDisabled]="item.primaryAction.kind === 'signalInterest' && (isInterestSubmissionPending(item) || isInterested(item))" (detail)="showDetails($event)" (action)="handleCardAction($event)"></wm-marketplace-card>
                            }
                        </div>
                    </section>

                    <section class="mp-section">
                        <div class="mp-section-heading">
                            <div>
                                <h2>Services & expertise</h2>
                                <p>Work directly with specialists. These items start a service request, not an automatic purchase.</p>
                            </div>
                        </div>
                        <div class="mp-card-grid mp-card-grid--two">
                            @for (item of serviceItems; track item.id) {
                                <wm-marketplace-card [item]="item" [pricingVisible]="pricingVisible" [primaryActionLabel]="actionLabel(item, item.primaryAction)" [primaryActionDisabled]="item.primaryAction.kind === 'signalInterest' && (isInterestSubmissionPending(item) || isInterested(item))" (detail)="showDetails($event)" (action)="handleCardAction($event)"></wm-marketplace-card>
                            }
                        </div>
                    </section>

                    <section class="mp-section mp-roadmap-section">
                        <div class="mp-roadmap-heading">
                            <span class="mp-roadmap-chip">Roadmap</span>
                            <div>
                                <h2>Explore what’s next</h2>
                                <p>These ideas are not available yet. Your interest helps us understand what to pursue.</p>
                            </div>
                        </div>
                        <div class="mp-card-grid mp-card-grid--two">
                            @for (item of roadmapItems; track item.id) {
                                <wm-marketplace-card [item]="item" [pricingVisible]="pricingVisible" [primaryActionLabel]="actionLabel(item, item.primaryAction)" [primaryActionDisabled]="item.primaryAction.kind === 'signalInterest' && (isInterestSubmissionPending(item) || isInterested(item))" (detail)="showDetails($event)" (action)="handleCardAction($event)"></wm-marketplace-card>
                            }
                        </div>
                    </section>
                }
            </main>
        } @else {
            <main class="mp-detail">
                <section class="mp-hero-grid">
                    <article class="mp-detail-card mp-item-hero">
                        <div class="mp-badges">
                            <span class="mp-type-badge mp-type-badge--{{selectedItem.kindStyle}}">{{selectedItem.kindLabel}}</span>
                            @if (selectedItem.presentationKind === 'roadmapIdea') {
                                <span class="mp-status-badge mp-status-badge--{{selectedItem.statusStyle}}">{{selectedItem.statusLabel}}</span>
                            }
                        </div>
                        <h1>{{selectedItem.title}}</h1>
                        <p class="mp-value-prop">{{selectedItem.valueProp}}</p>
                        <div class="mp-item-tags" role="list" aria-label="Tags">
                            @for (tag of selectedItem.tags; track tag.key) {
                                <span role="listitem">{{tag.label}}</span>
                            }
                        </div>
                        <p class="mp-provider"><fa-icon icon="user" aria-hidden="true"></fa-icon><span>Provided by <strong>{{selectedItem.provider}}</strong></span></p>
                    </article>

                    <aside class="mp-detail-card mp-action-panel">
                        @if (selectedItem.presentationKind === 'roadmapIdea') {
                            <p class="mp-eyebrow">Status</p>
                            <h2 class="mp-roadmap-status"><span></span>{{selectedItem.statusLabel}}</h2>
                            <div class="mp-interest-count"><strong>{{interestCount(selectedItem)}}</strong> people interested so far</div>
                        } @else if (!pricingVisible) {
                            <p class="mp-eyebrow">Commercial</p>
                            <h2>Pricing according to your agreement</h2>
                            <p>Request this item to confirm its scope and terms.</p>
                        } @else if (selectedItem.presentationKind === 'service') {
                            <p class="mp-eyebrow">Engagement</p>
                            <h2>{{selectedItem.commercialSummary}}</h2>
                            <p>{{selectedItem.commercialNote}}</p>
                        } @else {
                            <p class="mp-eyebrow">Commercial</p>
                            <h2>{{selectedItem.commercialSummary}}</h2>
                            <p>{{selectedItem.commercialNote}}</p>
                        }
                        @for (action of selectedItem.actions; track action.id) {
                            <button type="button" [class.mp-primary-button]="action.primary" [class.mp-secondary-button]="!action.primary" [disabled]="action.kind === 'signalInterest' && (isInterestSubmissionPending(selectedItem) || isInterested(selectedItem))" (click)="handleAction(selectedItem, action)">{{actionLabel(selectedItem, action)}}</button>
                        }
                        @if (selectedItem.presentationKind !== 'roadmapIdea' && selectedItem.presentationKind !== 'service') {
                            <small>A purchasing administrator handles the commercial steps.</small>
                        }
                    </aside>
                </section>

                <wm-marketplace-content [blocks]="selectedItem.body"></wm-marketplace-content>

                @if (relatedItems(selectedItem).length > 0) {
                    <section class="mp-related">
                        <p class="mp-eyebrow">Keep exploring</p>
                        <h2>Related in the marketplace</h2>
                        <p>Others looking at {{selectedItem.title}} often explore these too.</p>
                        <div class="mp-card-grid mp-card-grid--two">
                            @for (item of relatedItems(selectedItem); track item.id) {
                                <wm-marketplace-card [item]="item" [pricingVisible]="pricingVisible" [primaryActionLabel]="actionLabel(item, item.primaryAction)" [primaryActionDisabled]="item.primaryAction.kind === 'signalInterest' && (isInterestSubmissionPending(item) || isInterested(item))" (detail)="showDetails($event)" (action)="handleCardAction($event)"></wm-marketplace-card>
                            }
                        </div>
                    </section>
                }
            </main>
        }
    </div>
</section>

<p-dialog
    [visible]="!!dialog"
    (visibleChange)="onDialogVisibilityChange($event)"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [dismissableMask]="!interactionSubmissionPending"
    [closable]="!interactionSubmissionPending"
    [closeAriaLabel]="'Close ' + dialogTitle"
    [closeOnEscape]="false"
    [focusOnShow]="true"
    [focusTrap]="true"
    [header]="dialogTitle"
    [style]="{width: 'min(640px, 90vw)', maxHeight: 'min(720px, 90vh)'}"
    [contentStyle]="{padding: '0', overflow: 'hidden'}"
    (onHide)="restoreDialogTriggerFocus()"
    appendTo="body">
    <div class="mp-dialog__content">
        @if (interactionError) {
            <div class="mp-dialog__error" role="alert"><fa-icon icon="exclamation-circle" aria-hidden="true"></fa-icon>{{interactionError}}</div>
        }

        @if (dialog === 'request') {
            <div class="mp-dialog__body">
                @if (requestStep === 1) {
                    <p>Choose one or more projects for this capability.</p>
                    @if (projectsLoading) {
                        <div class="mp-inline-notice" aria-live="polite">
                            <fa-icon icon="spinner" animation="spin" aria-hidden="true"></fa-icon>
                            Loading projects…
                        </div>
                    } @else if (projectsLoadError) {
                        <div class="mp-inline-notice mp-inline-notice--error" role="alert">
                            <fa-icon icon="exclamation-circle" aria-hidden="true"></fa-icon>
                            <span>Projects could not be loaded.</span>
                            <button pButton type="button" class="mp-secondary-button" (click)="loadProjects()">Try again</button>
                        </div>
                    } @else if (projectTargetOptions.length > 0) {
                        <div class="mp-field mp-project-target-field">
                            <label id="marketplace-project-target-label" for="marketplace-project-target">Projects</label>
                            <p-multiSelect
                                inputId="marketplace-project-target"
                                ariaLabelledBy="marketplace-project-target-label"
                                ariaFilterLabel="Search projects"
                                [options]="projectTargetOptions"
                                [(ngModel)]="selectedProjectTargetValues"
                                optionLabel="label"
                                optionValue="value"
                                [filter]="true"
                                filterBy="label"
                                filterPlaceHolder="Search projects"
                                [showToggleAll]="false"
                                [maxSelectedLabels]="2"
                                selectedItemsLabel="{0} projects selected"
                                placeholder="Select one or more projects"
                                emptyFilterMessage="No matching projects"
                                scrollHeight="280px"
                                [virtualScroll]="true"
                                [virtualScrollItemSize]="39"
                                appendTo="body"
                                [baseZIndex]="1300"
                                [disabled]="interactionSubmissionPending"
                                [style]="{width: '100%'}"
                                (onChange)="onProjectTargetSelectionChange($event.value)">
                            </p-multiSelect>
                            @if (isCustomerAdmin) {
                                <small>As an administrator, you can request this for every project at once.</small>
                            } @else {
                                <small>Only projects you are a member of are available.</small>
                            }
                        </div>
                    } @else {
                        <div class="mp-inline-notice"><fa-icon icon="info-circle" aria-hidden="true"></fa-icon>No project is available for this request.</div>
                    }
                } @else if (requestStep === 2) {
                    <label class="mp-field">
                        <span>Anything the reviewer should know? <small>Optional</small></span>
                        <textarea pTextarea rows="6" maxlength="4000" [disabled]="interactionSubmissionPending" [(ngModel)]="requestNote" placeholder="Add context, timing, or expected usage…"></textarea>
                    </label>
                    @if (requestAction?.requiresApproval) {
                        <div class="mp-inline-notice mp-inline-notice--warning">
                            <fa-icon icon="info-circle" aria-hidden="true"></fa-icon>
                            This item needs purchasing approval. Submitting does not start a paid subscription.
                        </div>
                    }
                } @else if (requestStep === 3) {
                    <p>Review the request before sending it to a purchasing administrator.</p>
                    <dl class="mp-review-list">
                        <div><dt>Item</dt><dd>{{dialogItem?.title}}</dd></div>
                        <div><dt>Projects</dt><dd>{{requestTargetLabel}}</dd></div>
                        <div><dt>Commercial</dt><dd>{{pricingVisible ? dialogItem?.commercialSummary : 'Pricing according to your agreement'}}</dd></div>
                        <div><dt>Note</dt><dd>{{requestNote.trim() || 'No note added'}}</dd></div>
                    </dl>
                } @else {
                    <div class="mp-success-state">
                        <span><fa-icon icon="check" aria-hidden="true"></fa-icon></span>
                        <h3>Request ready for review</h3>
                        <p>It is now recorded in webmate and listed under Requests with the status “Pending review”.</p>
                        <ol>
                            <li>Testfabrik support can review the requested scope.</li>
                            <li>Commercial details are confirmed as a follow-up.</li>
                            <li>You receive an update when access can be prepared.</li>
                        </ol>
                    </div>
                }
            </div>
        } @else if (dialog === 'consultation') {
            <div class="mp-dialog__body">
                <p>Tell Testfabrik what you want to achieve. A specialist can then help shape the right scope.</p>
                <label class="mp-field">
                    <span>What would you like to discuss? <small>Optional</small></span>
                    <textarea pTextarea rows="7" maxlength="4000" [disabled]="interactionSubmissionPending" [(ngModel)]="consultationMessage" placeholder="Goals, current setup, timing, or questions…"></textarea>
                </label>
                <div class="mp-inline-notice"><fa-icon icon="info-circle" aria-hidden="true"></fa-icon>The request is recorded for Testfabrik support. It does not automatically start an engagement.</div>
            </div>
        } @else if (dialog === 'feedback') {
            <div class="mp-dialog__body">
                <p>What problem should {{dialogItem?.title}} solve for your team?</p>
                <label class="mp-field">
                    <span>Your feedback</span>
                    <textarea pTextarea rows="7" maxlength="4000" [disabled]="interactionSubmissionPending" [(ngModel)]="feedbackMessage" placeholder="Describe the workflow, problem, or outcome that matters to you…"></textarea>
                </label>
            </div>
        } @else if (dialog === 'requests') {
            <div class="mp-dialog__body">
                <p class="mp-dialog__intro">{{isCustomerAdmin ? 'Open Marketplace requests for your organization.' : 'Your open Marketplace requests.'}}</p>
                @if (isRequestsLoading) {
                    <div class="mp-empty-state mp-empty-state--compact" aria-live="polite">
                        <h3>Loading requests…</h3>
                    </div>
                } @else if (requestsLoadError) {
                    <div class="mp-error-state mp-error-state--compact" role="alert">
                        <fa-icon icon="exclamation-circle" aria-hidden="true"></fa-icon>
                        <h3>Requests unavailable</h3>
                        <p>{{requestsLoadError}}</p>
                        <button pButton type="button" class="mp-secondary-button" (click)="retryMarketplaceRequests()">Try again</button>
                    </div>
                } @else if (requests.length > 0) {
                    <div class="mp-request-list">
                        @for (request of requests; track request.id) {
                            <article>
                                <div><strong>{{request.itemTitleSnapshot}}</strong><span>{{requestTargetDisplayLabel(request.target)}}</span></div>
                                <span class="mp-pending-badge">Pending review</span>
                            </article>
                        }
                    </div>
                    @if (requestsLoadMoreError) {
                        <div class="mp-dialog__error mp-dialog__error--inline" role="alert"><fa-icon icon="exclamation-circle" aria-hidden="true"></fa-icon>{{requestsLoadMoreError}}</div>
                    }
                    @if (hasMoreRequests) {
                        <button pButton type="button" class="mp-secondary-button" [disabled]="isMoreRequestsLoading" (click)="loadMoreMarketplaceRequests()">
                            {{isMoreRequestsLoading ? 'Loading…' : 'Load more'}}
                        </button>
                    }
                } @else {
                    <div class="mp-empty-state mp-empty-state--compact">
                        <fa-icon icon="clipboard-check" aria-hidden="true"></fa-icon>
                        <h3>No requests yet</h3>
                        <p>Capability and solution requests will appear here.</p>
                    </div>
                }
            </div>
        }
    </div>

    <ng-template pTemplate="footer">
        <div class="mp-dialog__footer">
            @if (dialog === 'request') {
                @if (requestStep < 4) {
                    <span>Step {{requestStep}} of 3 · {{requestStep === 1 ? 'Grant target' : (requestStep === 2 ? 'Context' : 'Review')}}</span>
                    <div>
                        @if (requestStep > 1) {
                            <button pButton type="button" class="mp-secondary-button" [disabled]="interactionSubmissionPending" (click)="previousRequestStep()">Back</button>
                        }
                        <button pButton type="button" class="mp-primary-button" [disabled]="!canContinueRequest" (click)="continueRequest()">{{interactionSubmissionPending ? 'Submitting…' : (requestStep === 3 ? 'Submit request' : 'Continue')}}</button>
                    </div>
                } @else {
                    <span>Request recorded</span>
                    <button pButton type="button" class="mp-primary-button" (click)="closeDialog(); openRequests()">View requests</button>
                }
            } @else if (dialog === 'consultation') {
                <span>Service request</span>
                <div>
                    <button pButton type="button" class="mp-secondary-button" [disabled]="interactionSubmissionPending" (click)="closeDialog()">Cancel</button>
                    <button pButton type="button" class="mp-primary-button" [disabled]="interactionSubmissionPending" (click)="submitConsultation()">{{interactionSubmissionPending ? 'Submitting…' : 'Request consultation'}}</button>
                </div>
            } @else if (dialog === 'feedback') {
                <span>Roadmap feedback</span>
                <div>
                    <button pButton type="button" class="mp-secondary-button" [disabled]="interactionSubmissionPending" (click)="closeDialog()">Cancel</button>
                    <button pButton type="button" class="mp-primary-button" [disabled]="interactionSubmissionPending || !feedbackMessage.trim()" (click)="submitFeedback()">{{interactionSubmissionPending ? 'Submitting…' : 'Send feedback'}}</button>
                </div>
            } @else if (dialog === 'requests') {
                @if (isRequestsLoading) {
                    <span>Loading requests…</span>
                } @else if (!requestsLoadError) {
                    <span>{{requests.length}}{{hasMoreRequests ? '+' : ''}} open {{requests.length === 1 && !hasMoreRequests ? 'request' : 'requests'}}</span>
                }
                <button pButton type="button" class="mp-primary-button" (click)="closeDialog()">Done</button>
            }
        </div>
    </ng-template>
</p-dialog>
